/**
 * Conversation panel: new-chat button, a client-side search filter, the
 * saved-conversation list, per-row open (loadConversation) + inline rename +
 * delete.
 *
 * - New: clears the working draft + active id (a fresh, unsaved chat).
 * - Search: filters the rendered list by title substring (mirrors chat.js's
 *   `#conv-search`). Pure client-side over the already-loaded list — no extra
 *   API call — so it is instant and works offline of the network.
 * - Open: fetches the conversation detail and loads its messages into the draft
 *   (useOpenConversation → loadConversation analogue).
 * - Rename: double-click (or the pencil) swaps the row title for an input;
 *   commit on blur/Enter PATCHes the title and refreshes the list.
 * - Delete: the trash affordance DELETEs the conversation (after a confirm) and,
 *   if it was the open one, resets the session to a fresh chat (mirrors chat.js's
 *   delete → "if it was active, start a new chat" behaviour).
 *
 * All server reads/writes go through the data-access hooks — the component
 * never touches the API client directly (andavo-dev convention).
 *
 * WHAT THIS IS NOT (2026-10-17, the "double menu"): it is not navigation and
 * not a second app shell. It used to be a copy of the shell's SideNav — a
 * `fixed` full-height column with its own <nav>, its own scrim over the whole
 * viewport (TopBar included) and its own Sign out. It is now a chat-local
 * secondary panel: an <aside> named "Conversations" holding a plain list, with
 * no sign-out (the TopBar account menu owns that).
 *
 * Responsive: at >= lg it is an in-flow column inside the chat surface, hidden
 * when `collapsed`. Below lg it is a sheet that slides over the chat surface
 * only (`absolute` inside ChatPage's `relative isolate` container, which sits
 * below the TopBar), revealed by `open`, with a chat-local tap-to-close scrim
 * and an in-header close button — so on a 390px phone the list never eats the
 * transcript's width. `open` is inert at >= lg and `collapsed` is inert below
 * it (the `lg:` CSS branch decides).
 */
import {
  CheckIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useOpenConversation } from '@/features/chat/data-access/useOpenConversation';
import {
  useConversations,
  useDeleteConversation,
  useUpdateConversation,
} from '@/features/chat/data-access/useConversations';
import { useSessionStore } from '@/features/chat/store/sessionStore';
import { Button, ScrollArea, Separator, Skeleton } from '@andavo/web-ui';

function ConversationRow({
  id,
  title,
  active,
  onOpen,
  onDelete,
}: {
  id: string;
  title: string;
  active: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const updateConversation = useUpdateConversation();
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);

  const startEdit = () => {
    setDraftTitle(title || '');
    setEditing(true);
  };

  const commit = () => {
    const next = draftTitle.trim();
    setEditing(false);
    if (next && next !== title) {
      updateConversation.mutate({ id, body: { title: next } });
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-1 py-1">
        <input
          autoFocus
          className="min-w-0 flex-1 rounded-md border border-border-action bg-surface-background px-2 py-1 font-inter text-content-primary text-sm focus:outline-none"
          onBlur={commit}
          onChange={(e) => setDraftTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              // A rename's Escape cancels ONLY the rename. preventDefault marks
              // it handled for the sheet's own Escape-to-close, but the window
              // stream-stop shortcut ignores defaultPrevented while streaming
              // (so Escape in the composer can always stop a reply) - so the
              // event must also not leave this input at all. Doing it here, at
              // the input, leaves Escape-to-stop in the composer untouched.
              e.preventDefault();
              e.stopPropagation();
              setEditing(false);
            }
          }}
          value={draftTitle}
        />
        <Button
          aria-label="Save title"
          onClick={commit}
          size="icon"
          variant="ghost"
        >
          <CheckIcon className="size-icon-sm" />
        </Button>
      </div>
    );
  }

  return (
    <div
      // data-testid="conversation-row" — stable row hook for the e2e sidebar
      // delete/rename specs (selectors.ts conversationRow).
      className={`group flex items-center gap-1 rounded-md transition-colors ${
        active ? 'bg-surface-action-subtle' : 'hover:bg-surface-background'
      }`}
      data-testid="conversation-row"
    >
      <button
        className={`min-w-0 flex-1 truncate px-3 py-2 text-left font-inter text-sm ${
          active ? 'text-content-action' : 'text-content-secondary'
        }`}
        onClick={onOpen}
        onDoubleClick={startEdit}
        type="button"
      >
        {title || 'Untitled'}
      </button>
      <button
        aria-label={`Rename ${title || 'conversation'}`}
        className="px-1 text-content-secondary opacity-0 transition-opacity hover:text-content-primary group-hover:opacity-100"
        onClick={startEdit}
        type="button"
      >
        <PencilSimpleIcon className="size-icon-sm" />
      </button>
      <button
        // data-testid="conversation-delete" — e2e sidebar spec delete action
        // (selectors.ts conversationDelete).
        aria-label={`Delete ${title || 'conversation'}`}
        className="px-1 pr-2 text-content-secondary opacity-0 transition-opacity hover:text-content-danger group-hover:opacity-100"
        data-testid="conversation-delete"
        onClick={onDelete}
        type="button"
      >
        <TrashIcon className="size-icon-sm" />
      </button>
    </div>
  );
}

export function ConversationSidebar({
  open = false,
  collapsed = false,
  isWide = true,
  returnFocusRef,
  onNavigate,
}: {
  /** Whether the sheet is shown (below lg only; inert at >= lg). */
  open?: boolean;
  /** Whether the desktop column is hidden (>= lg only; inert below lg). */
  collapsed?: boolean;
  /**
   * Whether the viewport is >= lg (the column layout). CSS decides what is
   * DRAWN, but `inert` / `aria-hidden` / focus are not CSS, so the component
   * must know which layout it is in: a closed sheet is parked off-canvas yet
   * would otherwise stay tabbable and readable by a screen reader.
   */
  isWide?: boolean;
  /** The control that opened the sheet; focus returns to it when it closes. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Called when the user picks a conversation or starts a new one, so the
   * parent can close the mobile drawer. No-op contribution at >= lg.
   */
  onNavigate?: () => void;
} = {}) {
  const { data: conversations, isLoading } = useConversations();
  const activeConversationId = useSessionStore((s) => s.activeConversationId);
  const setActiveConversation = useSessionStore((s) => s.setActiveConversation);
  const reset = useSessionStore((s) => s.reset);
  const openConversation = useOpenConversation();
  const deleteConversation = useDeleteConversation();

  const [query, setQuery] = useState('');

  // ── Sheet accessibility (below lg) ───────────────────────────────────────
  // `hidden`: the panel is not on screen in the current layout, so it must be
  // out of the tab order and the accessibility tree (the closed sheet is only
  // translated off-canvas; a collapsed column is lg:hidden, but say it twice).
  const hidden = isWide ? collapsed : !open;
  const sheetShown = !isWide && open;
  const asideRef = useRef<HTMLElement>(null);
  const wasSheetShown = useRef(false);

  // Opening moves focus into the sheet (onto the panel itself, which is a named
  // landmark, so a screen reader announces "Conversations"); closing returns it
  // to the control that opened it - unless the user already moved focus
  // somewhere real. The sheet is inert while closed and the page behind is
  // inert while open, so this effect runs AFTER the commit that flipped both.
  useEffect(() => {
    if (sheetShown) {
      wasSheetShown.current = true;
      // preventScroll: focusing a just-revealed, translated panel inside an
      // overflow-hidden parent can otherwise scroll that parent sideways.
      asideRef.current?.focus({ preventScroll: true });
      return;
    }
    if (!wasSheetShown.current) return;
    wasSheetShown.current = false;
    const active = document.activeElement;
    if (
      !active ||
      active === document.body ||
      asideRef.current?.contains(active)
    ) {
      returnFocusRef?.current?.focus();
    }
  }, [sheetShown, returnFocusRef]);

  // Escape closes the sheet from anywhere on the page (the page behind it is
  // inert, so focus is in the sheet or has dropped to <body>). Listening on
  // `document`, in the bubble phase, is deliberate: the rename input's own
  // Escape (React, below the document) runs first and marks the event handled
  // with preventDefault. (The TopBar account menu also listens on `document`
  // and does not preventDefault, so one Escape closes it AND the sheet - fine.)
  // stopPropagation here keeps the window-level chat shortcuts (Esc = stop the
  // stream / close the artifacts panel) from ALSO firing on the same keystroke.
  useEffect(() => {
    if (!sheetShown) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      onNavigate?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [sheetShown, onNavigate]);

  // Client-side filter over the already-loaded list — instant, no extra fetch.
  // Matches chat.js's substring filter on the conversation title.
  const filtered = useMemo(() => {
    const all = conversations ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => (c.title || '').toLowerCase().includes(q));
  }, [conversations, query]);

  const handleDelete = (id: string, title: string) => {
    // A guard so a stray click never silently destroys history. window.confirm
    // is the same affordance the static chat.js used for destructive deletes.
    const ok =
      typeof window === 'undefined' ||
      window.confirm(`Delete "${title || 'this conversation'}"?`);
    if (!ok) return;
    deleteConversation.mutate(id, {
      onSuccess: () => {
        // If we deleted the conversation we were looking at, drop back to a
        // fresh, unsaved chat (mirrors chat.js: a deleted-active → new chat).
        if (id === activeConversationId) {
          reset();
          setActiveConversation(null);
        }
      },
      onError: () => {
        toast.error('Could not delete the conversation. Please try again.');
      },
    });
  };

  const startNewConversation = () => {
    reset();
    setActiveConversation(null);
    onNavigate?.();
  };

  return (
    // Mobile-first: below `lg` the panel is a sheet over the chat surface. The
    // wrapper is `display: contents`, so the scrim and the <aside> are laid out
    // by ChatPage's flex row directly; at >= lg the scrim is hidden and the
    // <aside> becomes a static in-flow column.
    <div className="contents">
      {/* Scrim — below `lg` only, while open. Chat-local (`absolute` inside the
          chat surface), so the TopBar stays visible and usable. */}
      <button
        aria-hidden="true"
        className={`absolute inset-0 z-10 bg-neutral-900/40 transition-opacity lg:hidden ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        data-testid="conversations-scrim"
        onClick={() => onNavigate?.()}
        // Don't let the click move focus onto the (aria-hidden) scrim: focus
        // stays in the sheet, so closing can hand it back to the toggle.
        onMouseDown={(e) => e.preventDefault()}
        tabIndex={-1}
        type="button"
      />
      {/* data-testid="conversation-sidebar" — the conversation panel the specs
          key on. z-20 keeps it under every shell layer (TopBar / nav scrim z-30,
          nav drawer z-40); ChatPage's `isolate` container guarantees it. */}
      <aside
        aria-labelledby="conversation-sidebar-title"
        className={`absolute inset-y-0 left-0 z-20 flex h-full w-72 max-w-[85vw] flex-col border-border-subtle border-r bg-surface-foreground transition-transform lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0 lg:shadow-none focus:outline-none ${
          // The shadow only while open: on the closed, off-canvas sheet it
          // bled a grey strip into the phone viewport's left edge.
          open ? 'translate-x-0 shadow-lg' : '-translate-x-full'
        }${collapsed ? ' lg:hidden' : ''}`}
        data-collapsed={collapsed ? 'true' : 'false'}
        data-open={open ? 'true' : 'false'}
        data-testid="conversation-sidebar"
        id="conversation-sidebar"
        // `inert` alone takes the subtree out of the tab order AND the
        // accessibility tree (every browser that has it treats inert content
        // as hidden). aria-hidden is deliberately NOT added: it would also blank
        // the panel's accessible name wherever it is merely parked.
        inert={hidden}
        ref={asideRef}
        tabIndex={-1}
      >
        <div className="flex items-center justify-between p-3">
          <h2
            className="font-sans font-semibold text-content-primary text-sm"
            id="conversation-sidebar-title"
          >
            Conversations
          </h2>
          <div className="flex items-center gap-1">
            <Button
              aria-label="New conversation"
              borderRadius="round"
              // data-testid="new-conversation-button" — e2e chat spec "New
              // conversation" reset action (selectors.ts newConversationButton).
              data-testid="new-conversation-button"
              onClick={startNewConversation}
              size="icon"
              variant="secondary"
            >
              <PlusIcon className="size-icon-sm" />
            </Button>
            {/* Close affordance — sheet only (below lg). */}
            <Button
              aria-label="Close conversations"
              className="lg:hidden"
              data-testid="close-conversations-button"
              onClick={() => onNavigate?.()}
              size="icon"
              variant="ghost"
            >
              <XIcon className="size-icon-sm" />
            </Button>
          </div>
        </div>
        <div className="px-3 pb-3">
          <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-background px-2 py-1.5 focus-within:border-border-action">
            <MagnifyingGlassIcon className="size-icon-sm text-content-secondary" />
            <input
              // data-testid="conversation-search" — e2e sidebar spec search input
              // (selectors.ts conversationSearch).
              aria-label="Search conversations"
              className="min-w-0 flex-1 bg-transparent font-inter text-content-primary text-sm placeholder:text-content-tertiary focus:outline-none"
              data-testid="conversation-search"
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conversations"
              type="search"
              value={query}
            />
          </div>
        </div>
        <Separator variant="subtle" />
        <ScrollArea className="flex-1">
          <div className="p-2">
            {isLoading ? (
              <div className="flex flex-col gap-1">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : (conversations ?? []).length === 0 ? (
              <p className="px-3 py-2 font-inter text-content-secondary text-sm">
                No conversations yet.
              </p>
            ) : filtered.length === 0 ? (
              <p
                className="px-3 py-2 font-inter text-content-secondary text-sm"
                data-testid="conversation-search-empty"
              >
                No conversations match "{query}".
              </p>
            ) : (
              // A plain list — not a <nav>: the shell rail is the app's one
              // navigation landmark. Sign-out lives in the TopBar account menu.
              <ul
                aria-label="Saved conversations"
                className="flex flex-col gap-1"
              >
                {filtered.map((c) => (
                  <li key={c.id}>
                    <ConversationRow
                      active={c.id === activeConversationId}
                      id={c.id}
                      onDelete={() => handleDelete(c.id, c.title)}
                      onOpen={() => {
                        void openConversation(c.id);
                        // Close the sheet so the picked conversation is
                        // immediately visible (no-op at >= lg).
                        onNavigate?.();
                      }}
                      title={c.title}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </ScrollArea>
      </aside>
    </div>
  );
}
